import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../../config/runtimeConfig';
import * as api from '../../features/collections/api';
import type { Collection, CollectionDetail, PlacedStudy, ServerVisibility, StudyGroup } from '../../features/collections/api';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { fromServerVisibility, toServerVisibility, type Visibility } from '../ui/Visibility';
import { toast } from '../ui/toast';
import { confirm } from '../ui/Confirm';
import { getShareLink, resetShareLink, shareUrl, uidSuffix, type ShareKind } from '../../features/share/api';
import { CATALOGUE_KEYS, invalidateCatalogue } from './freshness';
import { forgetUploadInto } from '../upload/store';
import type { ExploreData, HomeData, PublicCollection } from './explore';
import { fixtureCover } from '../dev/fixtures';

export type { Collection, CollectionDetail, PlacedStudy, StudyGroup };

/**
 * A study's cover for a signed-in page, by public id: the first of its images
 * the caller may see. Not the DICOMweb thumbnail, which is addressed by UID and
 * answers only for the workspace it is mounted on.
 */
export function studyThumbnail(studyId: string): string {
  if (import.meta.env.DEV && window.__TR_FIXTURES) return fixtureCover(studyId) ?? '#e8e8e6';
  return `${getConfig().apiRoot}/studies/${encodeURIComponent(studyId)}/thumbnail`;
}

/** The viewer's address for a study (by public id); `series` opens it on that image. */
export function studyPath(studyId: string, series?: string): string {
  return `${routeBase()}/studies/${encodeURIComponent(studyId)}${series ? `?series=${encodeURIComponent(series)}` : ''}`;
}

/**
 * Open a study (by public id) the way the account prefers: in this tab, or
 * (Settings, "Open the image viewer in a new tab") in a new one, keeping the
 * place in the collection. `series` opens it on that image.
 */
export function useOpenStudy(): (studyId: string, series?: string) => void {
  const nav = useNavigate();
  const qc = useQueryClient();
  return (studyId, series) => {
    const path = studyPath(studyId, series);
    const me = qc.getQueryData<{ prefs?: Record<string, unknown> } | null>(['session']);
    if (me?.prefs?.openViewerNewTab) {
      const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
      window.open(`${base}${path}`, '_blank', 'noopener');
      return;
    }
    nav(path);
  };
}

/** Route prefix: '/dev' in fixture mode so in-page links stay in it. */
export function routeBase(): string {
  return import.meta.env.DEV && window.__TR_FIXTURES ? '/dev' : '';
}

/** "Aug 30, 2026" */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The name a study card shows: the community Study ID, else the description,
 * else the tail of its public id — the one id every page has (Explore, Home
 * and search no longer carry the UID).
 */
export function studyName(s: Pick<PlacedStudy, 'study_id' | 'description' | 'public_id'>): string {
  return s.study_id || s.description || s.public_id.slice(-8);
}

export function collectionVis(c: { visibility: ServerVisibility }): Visibility {
  return fromServerVisibility(c.visibility);
}

const KEYS = {
  list: (ws: string | undefined, q: api.ListCollectionsQuery) => ['collections', ws, q] as const,
  detail: (id: string) => ['collection', id] as const,
  writableTargets: ['collection-targets', { scope: 'writable' }] as const,
};

export function useCollections(query: Omit<api.ListCollectionsQuery, 'workspace'>) {
  const { current, ready } = useSelectedWorkspace();
  const ws = current?.public_id;
  return useQuery({
    queryKey: KEYS.list(ws, query),
    queryFn: () => api.listCollections({ ...query, workspace: ws }),
    enabled: ready && !!ws,
    // The previous page stands in while the next loads — within ONE workspace.
    // Across a workspace switch it was the wrong workspace's answer: its rows
    // under the new workspace's name, and its `editable`, so somebody who
    // edits A and only views B was offered New collection and Delete in B
    // until B's page arrived.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === ws ? prev : undefined),
  });
}

export function useCollection(publicId: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(publicId ?? ''),
    queryFn: () => api.getCollection(publicId!, 500),
    enabled: !!publicId,
  });
}

/** The Move and Duplicate menus' targets: every workspace the caller may write. */
export function useWritableTargets() {
  return useQuery({
    queryKey: KEYS.writableTargets,
    queryFn: api.listWritableTargets,
    staleTime: 30_000,
  });
}

/**
 * The toast after copying a selection. Into another workspace the copied
 * studies are there at once but their images are not — each appears when its
 * copy finishes — so the person is told, rather than left to open an empty
 * study and think the copy failed.
 */
export function duplicatedMessage(r: Pick<api.DuplicateResult, 'studies' | 'groups' | 'copying'>, target: string, workspace?: string): string {
  const n = r.studies + r.groups;
  const done = `Copied ${n} ${n === 1 ? 'item' : 'items'} to ${target}`;
  if (!(r.copying > 0)) return done;
  return `${done}. The images are being copied to ${workspace ?? 'the other workspace'} and appear when done`;
}

/**
 * The toast after copying a whole collection into another workspace. The new
 * collection is there at once; its images follow, so the person is told rather
 * than left to open an empty collection and think the copy failed.
 */
export function copiedCollectionMessage(r: Pick<api.CollectionCopy, 'collection' | 'studies' | 'copying'>, workspace: string): string {
  const n = r.studies;
  const done = `Copied ${n} ${n === 1 ? 'study' : 'studies'} into “${r.collection.name}” in ${workspace}`;
  return r.copying > 0 ? `${done}. The images are being copied and appear when done` : done;
}

function fail(e: unknown) {
  toast(e instanceof Error ? e.message : 'Something went wrong');
}

/**
 * Every mutation refreshes EVERY catalogue view (freshness.ts): the list and
 * the collection page, and also Explore, Home, search, the shared collection
 * page, the study header and Usage, which used to keep their old copy until a
 * manual reload.
 */
function useInvalidate() {
  const qc = useQueryClient();
  return () => invalidateCatalogue(qc);
}

type Likeable = { liked: boolean; like_count: number };
type Patch<T> = (x: T) => T;

/**
 * Apply a change to a collection or a study in every cached view that shows
 * it, so the page the person is on and every other page agree at once.
 * Caches are read-only data; each updater returns a new object.
 */
function patchEverywhere(
  qc: ReturnType<typeof useQueryClient>,
  kind: 'collection' | 'study',
  id: string,
  patch: Patch<Record<string, unknown>>,
): void {
  const one = <T,>(x: T, match: boolean): T => (match ? (patch(x as unknown as Record<string, unknown>) as unknown as T) : x);
  const col = <T extends { public_id?: string; publicId?: string }>(x: T): T => one(x, kind === 'collection' && (x.public_id ?? x.publicId) === id);
  const st = <T extends { public_id: string }>(x: T): T => one(x, kind === 'study' && x.public_id === id);
  qc.setQueriesData<api.CollectionPage>({ queryKey: ['collections'] }, (old) => (old ? { ...old, collections: old.collections.map(col) } : old));
  qc.setQueriesData<CollectionDetail>({ queryKey: ['collection'] }, (old) => (old ? { ...col(old), studies: old.studies.map(st) } : old));
  qc.setQueriesData<ExploreData | null>({ queryKey: ['explore'] }, (old) => (old ? { ...old, collections: old.collections.map(col), studies: old.studies.map(st) } : old));
  qc.setQueriesData<PublicCollection | null>({ queryKey: ['public-collection'] }, (old) => (old ? { ...col(old), studies: old.studies.map(st) } : old));
  qc.setQueriesData<HomeData | null>({ queryKey: ['home'] }, (old) => (old ? { ...old, recentCollections: old.recentCollections.map(col), recentStudies: old.recentStudies.map(st) } : old));
  for (const key of ['study-head']) {
    qc.setQueriesData<{ public_id: string } | null>({ queryKey: [key] }, (old) => (old ? st(old) : old));
  }
}

/**
 * The studies a `withStudies` group delete takes with it, by public id.
 *
 * Read from the collection detail the page is already showing. The ungrouped
 * band is `group_public_id: null` and is never taken, and neither is another
 * group's — the server deletes only this group's rows, and forgetting an
 * upload aimed anywhere else would cancel work that is still perfectly valid.
 */
export function studiesTakenWithGroup(detail: CollectionDetail | undefined, groupId: string): string[] {
  return (detail?.studies ?? []).filter((s) => s.group_public_id === groupId).map((s) => s.public_id);
}

/**
 * `studies` with `ids` taken out and put back, in the order given, as one block
 * in band `groupId`: right after `afterId`, right before `beforeId`, or at the
 * end of the band — what the server does with the same drop.
 */
export function placeStudies(
  studies: PlacedStudy[],
  v: { ids: string[]; groupId: string | null; afterId?: string; beforeId?: string },
): PlacedStudy[] {
  const moving = new Set(v.ids);
  const byId = new Map(studies.map((s) => [s.public_id, s]));
  const block = v.ids.flatMap((id) => {
    const s = byId.get(id);
    return s ? [{ ...s, group_public_id: v.groupId }] : [];
  });
  const rest = studies.filter((s) => !moving.has(s.public_id));
  const anchor = v.afterId ?? v.beforeId;
  let at = anchor ? rest.findIndex((s) => s.public_id === anchor && s.group_public_id === v.groupId) : -1;
  if (at >= 0 && v.afterId) at += 1;
  if (at < 0) {
    // The end of the band: after its last study, else where the band begins
    // (groups in their order, ungrouped last — the array's own order).
    const last = rest.map((s) => s.group_public_id).lastIndexOf(v.groupId);
    at = last >= 0 ? last + 1 : v.groupId === null ? rest.length : 0;
  }
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}

export function useCollectionMutations() {
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  const { current } = useSelectedWorkspace();
  const ws = current?.public_id;
  const common = { onError: fail, onSettled: () => invalidate() };
  // Shown at once, then confirmed by the refetch onSettled triggers. In-flight
  // reads are cancelled first so an older answer cannot land on top.
  const optimistic = (kind: 'collection' | 'study', id: string, patch: Patch<Record<string, unknown>>) =>
    qc.cancelQueries({ predicate: (q) => (CATALOGUE_KEYS as readonly string[]).includes(q.queryKey[0] as string) }).then(() => patchEverywhere(qc, kind, id, patch));
  return {
    create: useMutation({ mutationFn: (name: string) => api.createCollection(name, ws), ...common }),
    rename: useMutation({ mutationFn: (v: { id: string; name: string }) => api.renameCollection(v.id, v.name), onMutate: (v) => optimistic('collection', v.id, (x) => ({ ...x, name: v.name })), ...common }),
    describe: useMutation({ mutationFn: (v: { id: string; description: string }) => api.describeCollection(v.id, v.description), onMutate: (v) => optimistic('collection', v.id, (x) => ({ ...x, description: v.description || null })), ...common }),
    setVisibility: useMutation({ mutationFn: (v: { id: string; vis: Visibility }) => api.setCollectionVisibility(v.id, toServerVisibility(v.vis)), onMutate: (v) => optimistic('collection', v.id, (x) => ({ ...x, visibility: toServerVisibility(v.vis) })), ...common }),
    remove: useMutation({ mutationFn: (id: string) => api.deleteCollection(id), onSuccess: (_r, id) => forgetUploadInto({ colId: id }), ...common }),
    // The collections list's bulk delete. Sequential, not Promise.all: each one
    // is a teardown, and firing a dozen purges at once is not something the
    // person can take back. One refusal does not stop the rest, either — a
    // collection holding a slide that is still converting answers 409, and
    // aborting there left the others undeleted AND the deleted ones still
    // ticked, so the next attempt failed on an id that was already gone. Each
    // id's verdict is reported instead, and the caller keeps only the failures
    // selected.
    removeMany: useMutation({
      mutationFn: async (ids: string[]) => {
        const failed: Array<{ id: string; message: string }> = [];
        let deleted = 0;
        for (const id of ids) {
          try {
            await api.deleteCollection(id);
            forgetUploadInto({ colId: id });
            deleted++;
          } catch (e) {
            failed.push({ id, message: e instanceof Error ? e.message : 'could not be deleted' });
          }
        }
        return { deleted, failed };
      },
      ...common,
    }),
    duplicate: useMutation({ mutationFn: (v: { studyIds?: string[]; groupIds?: string[]; collectionId: string; studyGroupId?: string }) => api.duplicateInto(v), ...common }),
    like: useMutation({
      mutationFn: (v: { kind: 'collection' | 'study'; id: string; liked: boolean }) => (v.liked ? api.unlike(v.kind, v.id) : api.like(v.kind, v.id)),
      // Optimistic: the heart flips at once, the way the prototype's does,
      // in EVERY view that shows the item (the card on Explore, the shared
      // page, Home, the study header), not only the page it was clicked on.
      onMutate: (v) =>
        optimistic(v.kind, v.id, (x) => {
          const l = x as unknown as Likeable;
          return { ...x, liked: !v.liked, like_count: Math.max(0, l.like_count + (v.liked ? -1 : 1)) };
        }),
      // No refetch on success: the optimistic flip IS the result. A failure
      // refetches so the heart shows the truth.
      onError: (e) => {
        fail(e);
        invalidate();
      },
    }),
    createGroup: useMutation({ mutationFn: (v: { id: string; name: string }) => api.createGroup(v.id, v.name), ...common }),
    renameGroup: useMutation({ mutationFn: (v: { id: string; groupId: string; name: string }) => api.renameGroup(v.id, v.groupId, v.name), ...common }),
    describeGroup: useMutation({ mutationFn: (v: { id: string; groupId: string; description: string }) => api.describeGroup(v.id, v.groupId, v.description), ...common }),
    // withStudies is the community page's delete: the group AND its slides go
    // (the confirm names them). Without it the slides drop to Ungrouped, which
    // is how a half-typed new group is cleaned up.
    deleteGroup: useMutation({
      mutationFn: (v: { id: string; groupId: string; withStudies?: boolean }) => api.deleteGroup(v.id, v.groupId, v.withStudies),
      // The group, and with withStudies the slides that went with it. Those
      // never pass through deleteStudies — the server takes them — so an
      // upload filing into one of them would have been left pointing at a
      // study that no longer exists. Their ids are read from the detail this
      // page is already showing rather than asked of the caller, which is one
      // more thing a new call site could forget; a cold cache is simply no
      // worse than before.
      onSuccess: (_r, v) => {
        forgetUploadInto({ grpId: v.groupId });
        if (!v.withStudies) return;
        const detail = qc.getQueryData<CollectionDetail>(KEYS.detail(v.id));
        for (const id of studiesTakenWithGroup(detail, v.groupId)) forgetUploadInto({ studyId: id });
      },
      ...common,
    }),
    moveGroup: useMutation({ mutationFn: (v: { id: string; groupId: string; afterId?: string; beforeId?: string }) => api.moveGroup(v.id, v.groupId, { afterId: v.afterId, beforeId: v.beforeId }), ...common }),
    moveGroupToCollection: useMutation({ mutationFn: (v: { id: string; groupId: string; target: string }) => api.moveGroupToCollection(v.id, v.groupId, v.target), ...common }),
    moveStudies: useMutation({ mutationFn: (v: { ids: string[]; collectionId: string; groupId?: string }) => api.moveStudies(v.ids, v.collectionId, v.groupId), ...common }),
    // Drag and drop on the collection page: the studies are shown in their new
    // place at once (the page draws each band in the order of `studies`), then
    // confirmed by the refetch.
    dropStudies: useMutation({
      mutationFn: (v: { id: string; ids: string[]; groupId: string | null; afterId?: string; beforeId?: string }) =>
        api.moveStudies(v.ids, v.id, v.groupId ?? undefined, { afterId: v.afterId, beforeId: v.beforeId }),
      onMutate: async (v) => {
        await qc.cancelQueries({ predicate: (q) => (CATALOGUE_KEYS as readonly string[]).includes(q.queryKey[0] as string) });
        qc.setQueryData<CollectionDetail>(KEYS.detail(v.id), (old) => (old ? { ...old, studies: placeStudies(old.studies, v) } : old));
      },
      ...common,
    }),
    copyCollection: useMutation({ mutationFn: (v: { id: string; workspace: string }) => api.copyCollectionTo(v.id, v.workspace), ...common }),
    deleteStudies: useMutation({ mutationFn: async (ids: string[]) => { for (const id of ids) { await api.deleteStudy(id); forgetUploadInto({ studyId: id }); } }, ...common }),
    setStudyVisibility: useMutation({ mutationFn: (v: { id: string; vis: Visibility }) => api.setStudyVisibility(v.id, toServerVisibility(v.vis)), ...common }),
  };
}

export function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text).catch(() => undefined);
  return Promise.resolve();
}

/**
 * Copy and reset an item's share link, from its visibility chip (admins and
 * editors; the server refuses anybody else). One stable link per collection or
 * study (decided 2026-09-15): copying twice gives the same link, and Reset is
 * the one way to make an old link stop working. A study's link names the
 * slide on screen, as a short suffix.
 */
export function useShareLinks() {
  return {
    copy: async (kind: ShareKind, id: string, name: string, slideUid?: string | null) => {
      try {
        const link = await getShareLink(kind, id);
        await copyText(shareUrl(link.path, kind === 'study' && slideUid ? uidSuffix(slideUid) : undefined));
        toast(`Link to ${name} copied`);
      } catch (e) {
        fail(e);
      }
    },
    reset: async (kind: ShareKind, id: string, name: string) => {
      const ok = await confirm({
        title: 'Reset this link?',
        body: 'The current link stops working for everyone who has it. A new link is copied.',
        action: 'Reset link',
        danger: true,
      });
      if (!ok) return;
      try {
        const link = await resetShareLink(kind, id);
        await copyText(shareUrl(link.path));
        toast(`New link to ${name} copied`);
      } catch (e) {
        fail(e);
      }
    },
  };
}
